import { useSegments } from "api/segments";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { useState } from "react";

export type Segment = {
  name: string;
  segmentId: string;
  description: string;
  active: boolean;
  createdAt: string;
  customersCount: number;
  averageTransactionAmount: number;
  averageTransactions: number;
  averageClv: number;
  currency: string;
  calculatedAt: string;
};

interface Props {
  segments: string[];
}

export const SegmentSelect = ({ segments }: Props) => {
  const { message } = useMessage();
  const [segmentSearch, setSegmentSearch] = useState("");
  const { useInfiniteSegmentsList } = useSegments();

  const {
    data: infiniteSegmentsList,
    size: segmentsSize,
    setSize: setSegmentsSize,
    isValidating,
  } = useInfiniteSegmentsList({
    filters: segmentSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: segmentSearch,
          },
        ]
      : [],
    order: "asc",
    sortKey: "name",
  });

  const { infiniteData: segmentsList, anyElementsToFetch: anySegmentsToFetch } =
    useManageInfiniteScroll(infiniteSegmentsList || []);

  const { useSegmentData } = useSegments();
  const { data: initialSegments } = useSegmentData<string | string[]>(segments);

  const initialValue = initialSegments
    ? Array.isArray(initialSegments)
      ? initialSegments.map((segment) => ({
          id: segment.segmentId,
          label: segment.name,
        }))
      : {
          id: initialSegments.segmentId,
          label: initialSegments.name,
        }
    : undefined;

  return (
    <SelectInput
      displaySearch
      data-test-id="audience-segments"
      items={
        segmentsList
          ? segmentsList.map(({ name, segmentId }) => ({
              id: segmentId,
              label: name,
            }))
          : []
      }
      label={<RequiredFieldLabel text={message({ id: "segments" })} />}
      name="audience.segments"
      initialValue={initialValue}
      anyElementsToFetch={anySegmentsToFetch}
      loadMoreElements={() =>
        !isValidating && setSegmentsSize(segmentsSize + 1)
      }
      asyncSearch={(searchedQuery: string) => setSegmentSearch(searchedQuery)}
    />
  );
};
